import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { FiSend } from 'react-icons/fi';
import { extractApiErrorMessage } from '@/lib/api';
import { track } from '@/lib/analytics';
import { CLIO_BLUE, CLIO_BLUE_SOFT } from '@/lib/clio-brand';
import {
  fetchClioMatterLink,
  fetchClioMatters,
  fetchClioStatus,
  formatClioMatterLabel,
  sendCounselReportToClio,
  setClioMatterLink,
  type ClioMatter,
} from '@/lib/clio';

type Props = {
  inviteId: number;
  /** Sections the invite allows (exchange / expense_disputes / hearing_prep). */
  availableSections: Array<'exchange' | 'expense_disputes' | 'hearing_prep'>;
};

const SECTION_LABELS: Record<string, string> = {
  exchange: 'Compliance Report',
  expense_disputes: 'Expense Dispute Summary',
  hearing_prep: 'Hearing Prep Brief',
};

/**
 * Matter picker + Send to Clio buttons for counsel reports on a client page.
 */
export function ClioSendReportControls({ inviteId, availableSections }: Props) {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const queryClient = useQueryClient();
  const [query, setQuery] = useState('');
  const [selectedMatter, setSelectedMatter] = useState<ClioMatter | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sendingSection, setSendingSection] = useState<string | null>(null);

  const statusQuery = useQuery({
    queryKey: ['clio-status'],
    queryFn: () => fetchClioStatus(getToken),
    enabled: isLoaded && !!isSignedIn,
    staleTime: 60_000,
  });

  const linkQuery = useQuery({
    queryKey: ['clio-matter-link', inviteId],
    queryFn: () => fetchClioMatterLink(getToken, inviteId),
    enabled: isLoaded && !!isSignedIn && !!statusQuery.data?.connected,
  });

  const mattersQuery = useQuery({
    queryKey: ['clio-matters', query],
    queryFn: () => fetchClioMatters(getToken, query || undefined),
    enabled: isLoaded && !!isSignedIn && !!statusQuery.data?.connected,
    staleTime: 30_000,
  });

  const linkedLabel = useMemo(() => {
    const link = linkQuery.data;
    if (!link) return null;
    return formatClioMatterLabel({
      id: link.clio_matter_id,
      display_number: link.clio_matter_display_number,
      description: link.clio_matter_description,
      client_name: link.clio_contact_name,
    });
  }, [linkQuery.data]);

  const saveMatterMutation = useMutation({
    mutationFn: (matter: ClioMatter) => setClioMatterLink(getToken, inviteId, matter),
    onSuccess: () => {
      track('Clio Matter Mapped', { source: 'pro_portal', invite_id: inviteId });
      queryClient.invalidateQueries({ queryKey: ['clio-matter-link', inviteId] });
      setMessage('Clio matter linked for this client.');
      setError(null);
    },
    onError: (e: unknown) => {
      setError(extractApiErrorMessage(e, 'Could not save Clio matter mapping.'));
    },
  });

  if (!statusQuery.data?.configured) return null;
  if (!statusQuery.data.connected) {
    return (
      <div
        className='mt-4 rounded-xl border border-dashed px-3 py-3'
        style={{ borderColor: `${CLIO_BLUE}66`, backgroundColor: CLIO_BLUE_SOFT }}
      >
        <p className='text-xs text-gray-700'>
          Connect Clio from the pro dashboard to send these reports onto a
          matter.
        </p>
      </div>
    );
  }

  if (availableSections.length === 0) return null;

  const activeMatterId =
    selectedMatter?.id ?? linkQuery.data?.clio_matter_id ?? null;

  return (
    <div
      className='mt-4 rounded-xl border px-3 py-3'
      style={{ borderColor: `${CLIO_BLUE}55`, backgroundColor: CLIO_BLUE_SOFT }}
    >
      <p className='text-xs font-semibold text-gray-900 mb-1'>
        <span style={{ color: CLIO_BLUE }}>Send to Clio</span>
      </p>
      <p className='text-[11px] text-gray-600 mb-2'>
        {linkedLabel
          ? `Linked matter: ${linkedLabel}`
          : 'Choose a Clio matter, then send a counsel PDF.'}
      </p>

      <div className='flex flex-col sm:flex-row gap-2 mb-3'>
        <input
          type='search'
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder='Search matters…'
          className='flex-1 rounded-lg border bg-white px-2.5 py-1.5 text-xs text-gray-900'
          style={{ borderColor: `${CLIO_BLUE}66` }}
        />
        <select
          className='flex-1 rounded-lg border bg-white px-2.5 py-1.5 text-xs text-gray-900'
          style={{ borderColor: `${CLIO_BLUE}66` }}
          value={selectedMatter?.id ?? linkQuery.data?.clio_matter_id ?? ''}
          onChange={(e) => {
            const id = Number(e.target.value);
            const matter = (mattersQuery.data?.matters ?? []).find((m) => m.id === id);
            if (matter) {
              setSelectedMatter(matter);
              saveMatterMutation.mutate(matter);
            }
          }}
        >
          <option value='' disabled>
            {mattersQuery.isLoading ? 'Loading matters…' : 'Select a matter'}
          </option>
          {(mattersQuery.data?.matters ?? []).map((m) => (
            <option key={m.id} value={m.id}>
              {formatClioMatterLabel(m)}
            </option>
          ))}
          {linkQuery.data &&
          !(mattersQuery.data?.matters ?? []).some(
            (m) => m.id === linkQuery.data!.clio_matter_id
          ) ? (
            <option value={linkQuery.data.clio_matter_id}>
              {linkedLabel}
            </option>
          ) : null}
        </select>
      </div>

      <div className='flex flex-wrap gap-2'>
        {availableSections.map((section) => (
          <button
            key={section}
            type='button'
            disabled={sendingSection !== null || activeMatterId == null}
            onClick={async () => {
              if (activeMatterId == null) return;
              setError(null);
              setMessage(null);
              setSendingSection(section);
              track('Send to Clio Clicked', {
                source: 'pro_portal',
                invite_id: inviteId,
                section,
              });
              try {
                const result = await sendCounselReportToClio(
                  getToken,
                  inviteId,
                  section,
                  activeMatterId
                );
                setMessage(
                  `Sent ${SECTION_LABELS[section] ?? section} to Clio (document #${result.document_id}).`
                );
                track('Counsel Report Sent to Clio', {
                  source: 'pro_portal',
                  invite_id: inviteId,
                  section,
                  matter_id: result.matter_id,
                });
              } catch (e: unknown) {
                setError(
                  extractApiErrorMessage(e, 'Could not send report to Clio.')
                );
              } finally {
                setSendingSection(null);
              }
            }}
            className='inline-flex items-center gap-1.5 rounded-xl bg-[#0070E0] px-3.5 py-2 text-[11px] font-semibold text-white shadow-sm hover:bg-[#0F478D] disabled:opacity-50'
          >
            <FiSend className='h-3 w-3' />
            {sendingSection === section
              ? 'Sending…'
              : `Send ${SECTION_LABELS[section] ?? section}`}
          </button>
        ))}
      </div>

      {message ? (
        <p className='mt-2 text-[11px] text-emerald-800'>{message}</p>
      ) : null}
      {error ? <p className='mt-2 text-[11px] text-red-700'>{error}</p> : null}
    </div>
  );
}
